import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import AppHeader from "@/components/AppHeader";
import BrandLogo from "@/components/BrandLogo";
import { ArrowRight } from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  show: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { delay: 0.05 * i, duration: 0.65, ease: [0.16, 1, 0.3, 1] }
  })
};

export default function Sobre() {
  return (
    <div className="min-h-screen bg-brand-bg text-white relative overflow-hidden" data-testid="sobre-page">
      <div className="absolute inset-0 starfield opacity-90 pointer-events-none" />
      <div className="absolute inset-0 bloom-top pointer-events-none" />

      <AppHeader />

      <main className="relative z-10 max-w-3xl mx-auto px-6 lg:px-10 pt-20 pb-24">
        <motion.div variants={fadeUp} initial="hidden" animate="show" className="text-center mb-14">
          <div className="pill-badge mb-6 mx-auto inline-flex"><span className="dot" /> Sobre</div>
          <h1 className="font-display text-4xl sm:text-6xl leading-[1.02] tracking-tight mb-5">
            Uma metodologia que virou <span className="pink-glow-text">plataforma.</span>
          </h1>
          <p className="text-brand-ink2 text-[16px] leading-relaxed max-w-xl mx-auto">
            A ValiosIA é o desdobramento digital do Instituto Valiosas de Treinamentos,
            fundado pela fisioterapeuta Gabriela Fachini.
          </p>
        </motion.div>

        <motion.div
          variants={fadeUp} initial="hidden" animate="show" custom={2}
          className="space-y-6 text-[15.5px] leading-[1.85] text-brand-ink2"
          data-testid="sobre-copy"
        >
          <p>
            A ValiosIA nasce de uma convicção simples: comunicação de qualidade, em saúde
            estética, começa com conhecimento técnico. Antes de ser uma plataforma, ela é o
            desdobramento de uma metodologia construída ao longo de anos de prática clínica e
            de ensino — a do <span className="text-white">Instituto Valiosas de Treinamentos</span>.
          </p>
          <p>
            <span className="text-white">Gabriela Fachini</span> é fisioterapeuta e esteticista
            cosmetóloga, especializada em eletrotermofototerapia e depilação a laser, com mais
            de oito anos de atuação e mais de cinco mil atendimentos realizados. Entusiasta da
            personalização do atendimento, desenvolveu um método próprio que privilegia a
            avaliação individualizada, o raciocínio clínico e a biossegurança — princípios que
            orientam resultados consistentes desde a primeira sessão.
          </p>
          <p>
            Desse trabalho nasceu o <span className="text-white">Método Valiosa de Depilação a
            Laser</span>, programa de formação do instituto que já capacitou mais de mil
            profissionais em todo o Brasil, com certificação reconhecida pelo MEC. Mais do que
            técnica, a formação abrange a construção de uma prática sustentável: ética,
            precificação justa, fidelização de pacientes e comunicação estratégica — porque
            autoridade profissional se constrói tanto na cabine de atendimento quanto na forma
            de se apresentar ao público.
          </p>
          <p>
            Esse compromisso com a qualificação do setor levou Gabriela a idealizar e presidir
            a <span className="text-white">ABRAED — Associação Brasileira de Epilação
            Definitiva</span>, criada em 2025 para promover formação qualificada, atualização
            baseada em evidências científicas e boas práticas em um mercado em rápida expansão.
          </p>
          <p>
            A <span className="text-white">ValiosIA</span> é a expressão dessa metodologia em
            forma de plataforma: agentes de inteligência artificial que traduzem os princípios
            do instituto — personalização, rigor técnico e comunicação responsável — em conteúdo
            estratégico para clínicas de depilação a laser. Roteiros de Reels, sequências de
            Stories e copy pensados para educar pacientes, construir autoridade e respeitar as
            normas de publicidade em saúde, em conformidade com as diretrizes do CFM e da Anvisa.
          </p>
          <p>
            Acreditamos que a clínica que comunica bem é aquela que domina o que faz. Por isso,
            cada conteúdo gerado pela ValiosIA parte do mesmo princípio que orienta o instituto
            desde o início: não existe receita de bolo — existe método, estudo e respeito por
            cada paciente.
          </p>
        </motion.div>

        <motion.div
          variants={fadeUp} initial="hidden" animate="show" custom={4}
          className="mt-14 rounded-3xl border border-brand-line bg-brand-surface/55 backdrop-blur-sm p-8 text-center"
        >
          <h2 className="font-display text-2xl text-white mb-3 tracking-tight">Fale com a gente</h2>
          <p className="text-[14px] text-brand-ink2 mb-6">
            Dúvidas, parcerias ou imprensa — escreva para o nosso time.
          </p>
          <a
            href="mailto:contato@valiosia.com.br"
            data-testid="sobre-contato"
            className="pill-dark-pink inline-flex items-center gap-2 px-7 py-3 text-[13.5px] font-ui"
          >
            contato@valiosia.com.br
          </a>
        </motion.div>

        <motion.div
          variants={fadeUp} initial="hidden" animate="show" custom={5}
          className="mt-10 text-center"
        >
          <Link
            to="/signup"
            data-testid="sobre-cta-signup"
            className="pill-pink inline-flex items-center gap-2 px-8 py-3.5 text-[14px] tracking-wide"
          >
            Configurar minha marca
            <ArrowRight className="w-4 h-4" />
          </Link>
        </motion.div>
      </main>

      <footer className="relative border-t border-brand-line py-10 z-10">
        <div className="max-w-3xl mx-auto px-6 flex flex-col items-center gap-3">
          <BrandLogo size="md" />
          <div className="flex gap-5 text-[13px] font-ui">
            <Link to="/termos" className="text-brand-ink2 hover:text-white transition-colors">Termos de Uso</Link>
            <Link to="/privacidade" className="text-brand-ink2 hover:text-white transition-colors">Política de Privacidade</Link>
          </div>
          <div className="text-[12px] text-brand-ink3 font-ui">
            © {new Date().getFullYear()} ValiosIA — Instituto Valiosas de Treinamentos
          </div>
        </div>
      </footer>
    </div>
  );
}
